/**
 * @voltxampere/web — Validation toast gate (Sprint 25 R2 lane 2)
 *
 * Bridge between `circuit-validator.ts` (pure validator) and the
 * Sprint 25 R2 lane 1 toast stack. Centralises the "validation fails →
 * toast + abort" pattern so the three runners (DC / Transient / AC)
 * stay terse.
 *
 * Errors push as `kind: "error"` with `duration: Infinity` (user must
 * acknowledge — broken topologies are persistent until edited away).
 * Warnings push as `kind: "warning"` with the default 4 s timer.
 *
 * The gate returns `true` when the circuit is safe to dispatch to the
 * engine. Callers that opt into the warning surface pass
 * `{ warnings: true }`; runners that prefer to silently degrade to a
 * solver-side warning omit it.
 *
 * Engine zero-diff: this module lives in apps/web. No engine src
 * mutation. Sentinel impact: none.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Circuit } from "@voltxampere/schema";

import { t } from "../i18n/index.js";
import { showToast } from "./toasts.js";
import { validateCircuit, type ValidationIssue } from "./circuit-validator.js";

interface GateOpts {
  /** When true, also surface warning-severity issues as toasts. */
  readonly warnings?: boolean;
}

function formatIssue(issue: ValidationIssue): string {
  return t(issue.messageKey as Parameters<typeof t>[0], issue.messageParams);
}

/**
 * Validate the circuit and surface any errors via the toast stack.
 * Returns `true` when the circuit is safe to dispatch to the engine
 * (zero error-severity issues), `false` otherwise.
 */
export function gateCircuitForEngine(circuit: Circuit, opts: GateOpts = {}): boolean {
  const result = validateCircuit(circuit);

  if (result.errors.length > 0) {
    const lines = result.errors.map(formatIssue);
    showToast({
      kind: "error",
      title: t("validation.toast.error.title"),
      message: lines.join(" · "),
      duration: Number.POSITIVE_INFINITY,
    });
  }

  if (opts.warnings === true && result.warnings.length > 0) {
    const lines = result.warnings.map(formatIssue);
    showToast({
      kind: "warning",
      title: t("validation.toast.warning.title"),
      message: lines.join(" · "),
    });
  }

  return result.valid;
}
